import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PackageCheck, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loading } from "@/components/Common";
import { StatusBadge, WarehouseBadge } from "@/components/Badges";
import { SimpleSelect } from "@/components/Filters";
import { ReceiveDialog } from "@/components/ReceiveDialog";
import { useAuth } from "@/context/AuthContext";
import { useLoad } from "@/hooks/useData";
import { api, errMsg } from "@/lib/api";
import { dateLabel } from "@/lib/format";
import { useVendors } from "@/components/VendorPicker";

const Stat = ({ label, value, testid, tone = "" }) => (
  <div className="panel p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className={`mt-1 font-mono text-2xl font-bold ${tone}`} data-testid={testid}>{value}</div></div>
);

const match = (o, f) => (f.vendor === "all" || o.supplier === f.vendor) && (f.status === "all" || o.status === f.status) && (f.wh === "all" || o.warehouse === f.wh)
  && (!f.overdue || o.overdue) && (!f.from || o.order_date >= f.from) && (!f.to || o.order_date <= f.to)
  && (!f.q || `${o.order_no} ${o.item_names}`.toLowerCase().includes(f.q.toLowerCase()));

export default function OpenOrdersTab() {
  const nav = useNavigate();
  const { can } = useAuth();
  const vendors = useVendors();
  const { data, reload } = useLoad(() => api.get("/po-receipts/open").then((r) => r.data));
  const [f, setF] = useState({ q: "", vendor: "all", status: "all", wh: "all", overdue: false, from: "", to: "" });
  const [order, setOrder] = useState(null);
  const rows = useMemo(() => (data?.rows || []).filter((o) => match(o, f)), [data, f]);
  if (!data) return <Loading />;
  const s = data.summary;
  const record = async (o) => {
    try { setOrder((await api.get(`/purchase-orders/${o.id}`)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Open POs" value={s.open} testid="open-stat-open" />
        <Stat label="Total pending qty" value={s.pending} testid="open-stat-pending" tone="text-amber-600" />
        <Stat label="Overdue POs" value={s.overdue} testid="open-stat-overdue" tone={s.overdue ? "text-red-600" : ""} />
        <Stat label="Receipts this week" value={s.week_receipts} testid="open-stat-week" />
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-60"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={f.q} onChange={set("q")} placeholder="PO no or item name" className="h-9 pl-8" data-testid="open-search" /></div>
        <SimpleSelect value={f.vendor} onChange={set("vendor")} testid="open-vendor" className="h-9 w-full sm:w-52" options={[["all", "All vendors"], ...vendors.map((v) => [v.name, v.name])]} />
        <SimpleSelect value={f.status} onChange={set("status")} testid="open-status" className="h-9 w-36" options={[["all", "All status"], ["pending", "Pending"], ["partial", "Partial"]]} />
        <SimpleSelect value={f.wh} onChange={set("wh")} testid="open-wh" className="h-9 w-32" options={[["all", "All WH"], ["A", "WH A"], ["B", "WH B"]]} />
        <Input type="date" value={f.from} onChange={set("from")} className="h-9 w-40" data-testid="open-from" />
        <Input type="date" value={f.to} onChange={set("to")} className="h-9 w-40" data-testid="open-to" />
        <label className="flex items-center gap-2 text-xs"><Checkbox checked={f.overdue} onCheckedChange={(v) => set("overdue")(!!v)} data-testid="open-overdue-only" /> Overdue only</label>
      </div>
      <div className="panel overflow-auto max-h-[calc(100vh-380px)]">
        <table className="data-table" data-testid="open-orders-table">
          <thead><tr><th>PO no</th><th>Type</th><th>Vendor</th><th>PO date</th><th>Due date</th><th>WH</th><th>Progress</th><th>Pending</th><th>Last receipt</th><th>Status</th><th></th></tr></thead>
          <tbody>{rows.map((o) => (
            <tr key={o.id} className="cursor-pointer hover:bg-muted/40" onClick={() => nav(`/po-receipts/po/${o.id}`)} data-testid={`open-row-${o.order_no}`}>
              <td className="font-mono font-bold text-primary">{o.order_no}</td>
              <td className="text-xs uppercase">{o.kind === "manufacturing" ? "MO" : "PO"}</td>
              <td className="wrap-cell min-w-[160px] text-xs">{o.supplier || "—"}</td>
              <td className="text-xs">{dateLabel(o.order_date)}</td>
              <td className={`text-xs ${o.overdue ? "font-semibold text-red-600" : ""}`} data-testid={`open-due-${o.order_no}`}>{o.expected_date ? dateLabel(o.expected_date) : "—"}{o.overdue && " · overdue"}</td>
              <td><WarehouseBadge value={o.warehouse} /></td>
              <td className="min-w-[140px]"><div className="h-2 rounded bg-muted"><div className="h-2 rounded bg-primary" style={{ width: `${o.pct}%` }} /></div><div className="font-mono text-[10px]">{o.received}/{o.ordered} · {o.pct}%</div></td>
              <td className="font-mono font-semibold text-amber-600">{o.pending}</td>
              <td className="text-xs">{o.last_receipt ? dateLabel(o.last_receipt) : "—"}</td>
              <td><StatusBadge value={o.status} /></td>
              <td onClick={(e) => e.stopPropagation()}>{can("orders", "edit") && <Button size="sm" variant="outline" onClick={() => record(o)} data-testid={`open-record-${o.order_no}`}><PackageCheck className="h-4 w-4" /> Record receipt</Button>}</td>
            </tr>))}
            {!rows.length && <tr><td colSpan={11} className="py-8 text-center text-sm text-muted-foreground">No open orders</td></tr>}</tbody>
        </table>
      </div>
      <ReceiveDialog open={!!order} order={order} onOpenChange={(o) => !o && setOrder(null)} onDone={reload} />
    </div>
  );
}
